<script setup lang="ts">
import {Pagination} from "@/data/base_classes/pagination";
import {resolveStatusColor, resolveStatusText} from "./constants";
import {SortItem} from "@/@core/types";
import {useFilterPaginationStore} from "./filters.store";
import {formatFiltersAsQueries} from "./functions";
import {router} from "@/plugins/1.router";
import {EntityClass} from "@/data/types/types.gen.d";
import {
  Column,
  TableRow,
  FilterConfig,
  Filter,
  TableConfig,
  TableMultiItemsAction,
  TableDefaultAction,
  FilterValue,
} from "./types.d";
import {toast} from "vue3-toastify";
import {loader} from "../loader/loader";

////////////////////////////////////////////////////////////
//////////////////// PROPS ///////////////////////////////
////////////////////////////////////////////////////////////
const props = defineProps<{
  store: any;
  entityName: string;
  columns: Column[];
  toTableRow: (item: any) => TableRow;
  hideFilterBar?: boolean;
  filtersConfig?: FilterConfig[] | Filter[];
  defaultActions?: TableConfig["defaultActions"];
  multiItemsActions?: TableConfig["multiItemsActions"];
  hideSelect?: boolean;
  disableSearch?: boolean;
  rowClickHandler?: (event: Event, row: { item: TableRow }) => void;
  order?: {
    by: string;
    asc: boolean;
  };
  pageSize?: number;
}>();

////////////////////////////////////////////////////////////
//////////////////// SETUP /////////////////////////////
////////////////////////////////////////////////////////////
const filterPaginationStore = useFilterPaginationStore();
const {t} = useI18n();

////////////////////////////////////////////////////////////
//////////////////// VARIABLES /////////////////////////////
////////////////////////////////////////////////////////////
const pagination = reactive(new Pagination());

const filters = ref<Filter[]>(
  (props.filtersConfig ?? []).map((x) => ({
    ...x,
    value: "value" in x ? x.value : getEmptyFilterValue(x),
  })),
);
const sortBy = ref<SortItem[]>([]);
const filtersActive = ref(false);
const prevQuery = reactive<{
  pagination: Pagination;
  filters: Filter[];
  searchQuery: string;
}>({
  pagination: new Pagination(),
  filters: [],
  searchQuery: "",
});
const tableRows = ref<TableRow[]>([]);
const loading = ref(false);
const isInitializing = ref(true);

////////////////////////////////////////////////////////////
//////////////////// SETUP ///////////////////////////////
////////////////////////////////////////////////////////////
const search = ref("");
const searching = ref(false);
const selected = ref<string[]>([]);
const showFilterBar = ref(false);

////////////////////////////////////////////////////////////
//////////////////// METHODS ///////////////////////////////
////////////////////////////////////////////////////////////
/**
 * Load the data from the store
 */
async function load(
  {
    pagination,
    filters,
    searchQuery,
  }: { pagination: Pagination; filters: Filter[]; searchQuery?: string },
  force = false,
): Promise<void> {
  // Start loading
  loading.value = true;

  // Query
  const queryResult = await query({pagination, filters, searchQuery}, force);

  // Set the table rows
  tableRows.value = queryResult.items.map(props.toTableRow);

  // Set the pagination next page
  pagination.has_next_page = queryResult.pagination.has_next_page;
  pagination.has_previous_page = queryResult.pagination.has_previous_page;

  // Save pagination and filters
  prevQuery.pagination = pagination.deepCopy() ?? prevQuery.pagination;
  prevQuery.filters = filters ?? [];
  prevQuery.searchQuery = searchQuery ?? prevQuery.searchQuery;

  // Stop loading
  loading.value = false;
  setTimeout(() => {
    loader.stop();
  }, 500);
}

/**
 * Retrieve the query
 */
async function query(
  {
    pagination,
    filters,
    searchQuery,
  }: { pagination: Pagination; filters: Filter[]; searchQuery?: string },
  force = false,
): Promise<{
  items: EntityClass[];
  pagination: Pagination;
  searchQuery?: string;
}> {
  // Collect query result
  let queryResult: {
    items: EntityClass[] | null;
    pagination?: Pagination;
  };

  // Retrieve all the items from the store
  if (searchQuery?.length) {
    queryResult = await props.store
      .search(searchQuery, pagination, force)
      .catch(handleQueryError);
  } else if (!filters?.length) {
    queryResult = await props.store
      .retrievePaginated(pagination, force)
      .catch(handleQueryError);
  } else {
    queryResult = await props.store
      .query(formatFiltersAsQueries(filters), pagination, force)
      .catch(handleQueryError);
  }

  // Handle query result
  if (queryResult.items === null) {
    return {items: [], pagination: pagination};
  }

  return {
    items: queryResult.items,
    pagination: queryResult.pagination ?? pagination,
  };
}

/**
 * Update the query
 * @param {Filter[]} fArr The filters to apply
 */
const updateQuery = ({
                       pagination,
                       filters,
                       searchQuery,
                     }: {
  pagination: Pagination;
  filters?: Filter[];
  searchQuery?: string;
}): void => {
  // Determine if the filters are active
  filtersActive.value = filters?.length ? true : false;

  // Check if parameters have changed
  if (
    loading.value ||
    (pagination.equals(prevQuery.pagination) &&
      filters?.length === prevQuery.filters.length &&
      filters?.every((f, i) => f.value === prevQuery.filters[i].value) &&
      searchQuery === prevQuery.searchQuery)
  ) {
    return;
  }

  // Load the data
  void load({
    pagination,
    filters: filters ?? [],
    searchQuery,
  });
};

/**
 * Handle the query error
 */
function handleQueryError(err: any): {
  items: EntityClass[] | null;
} {
  console.error(err);
  if (err?.response?.data?.details?.includes("no matching index found")) {
    toast.error(
      t(
        "No matching index was found, the index is now being created. This might take 5 to 10 minutes, please try again later!",
      ),
    );
  } else if (
    err?.response?.data?.details?.includes(
      "index for this query is not ready to serve",
    )
  ) {
    toast.error(
      t("The index is currently being created. Please try again later!"),
    );
  } else if (err?.response?.status === 404) {
    toast.warn(t("No items found matching the search criteria"));
  } else {
    toast.error(err.message);
  }
  return {items: null};
}

/**
 * Performs an action with optional parameters.
 * @param {TableMultiItemsAction | TableDefaultAction} action - The action to perform.
 * @param {any} params - The optional parameters for the action.
 */
async function perform(
  action: TableMultiItemsAction | TableDefaultAction,
  params?: any,
): Promise<void> {
  loading.value = true;
  await action.handler(params, () => {
    loading.value = false;
  });
}

/**
 * Executes an action on an item
 * @param {(item: any) => Promise<void>} action - The action to perform.
 * @param {any} item - The item to perform the action on.
 */
async function execute(
  handler: (item: any, done?: () => void) => Promise<void>,
  item: any,
): Promise<void> {
  loading.value = true;
  await handler(item, () => {
    loading.value = false;
    loader.stop();
  });
}

/**
 * Applies filters to the data table.
 * @param {Filter[]} filters - The filters to apply.
 */
function applyFilters(filters: Filter[]): void {
  // Determine if filters are active
  let activeFilters = filters.filter((x) => !filterIsEmpty(x));

  // Remove search filter
  if (activeFilters.length > 0) {
    search.value = "";
    searching.value = search.value.length > 0;
  }

  // Save the filters and pagination to the filterPaginationStore if a key is provided
  if (props.entityName) {
    filterPaginationStore.set(props.entityName, {
      pagination,
      filters,
    });
  }

  // Write the pagination to the query
  router.replace({
    query: {
      ...router.currentRoute.value.query,
      search: undefined,
      pagination: pagination.toRouteQuery(),
    },
  });

  void updateQuery({
    pagination,
    filters: activeFilters,
  });
}

/**
 * Searches the data table.
 */
function performSearch(): void {
  // Remove filters
  filters.value.forEach((filter) => {
    filter.value = getEmptyFilterValue(filter);
  });

  // Set searching state based on search value
  searching.value = search.value.length > 0;

  // Always go to the first page
  pagination.page_number = 1;

  // Write the pagination to the query
  router.replace({
    query: {
      ...router.currentRoute.value.query,
      queries: undefined,
      search: search.value || undefined,
      pagination: pagination.toRouteQuery(),
    },
  });

  void updateQuery({
    pagination,
    filters: [],
    searchQuery: search.value,
  });
}

/**
 * Clears all filters from the data table.
 */

function clearFilters(): void {
  filters.value.forEach((filter) => {
    filter.value = getEmptyFilterValue(filter);
  });

  // Clear search
  search.value = "";
  searching.value = false;

  // Remove from query
  router.replace({
    query: {
      ...router.currentRoute.value.query,
      queries: undefined,
      pagination: pagination.toRouteQuery(),
    },
  });

  // Apply no filters
  applyFilters([]);
}

/**
 * Returns the empty value for a filter.
 * @param {FilterConfig | Filter} filter - The filter to get the empty value for.
 * @returns {any} The empty value for the filter.
 */
function getEmptyFilterValue(filter: FilterConfig | Filter): any {
  if (filter.defaultValue !== undefined) {
    return filter.defaultValue;
  }

  if (filter.type === "range") {
    return [filter.min, filter.max];
  } else if (filter.multiple) {
    return [];
  } else {
    return FilterValue.EMPTY;
  }
}

/**
 * Checks if a filter is empty.
 * @param {Filter} filter - The filter to check.
 * @returns {boolean} True if the filter is empty, false otherwise.
 */
function filterIsEmpty(filter: Filter): boolean {
  if (filter.type === "range") {
    return filter.value[0] === filter.min && filter.value[1] === filter.max;
  } else if (filter.multiple) {
    return filter.value.length === 0;
  } else {
    return filter.value === "" || filter.value === FilterValue.EMPTY;
  }
}

/**
 * Sets the order by column and sort direction.
 * @param {string} key - The key of the column to sort by.
 * @param {boolean} asc - The sort direction (true for ascending, false for descending).
 */
function setOrderBy(key: string, asc: boolean): void {
  // Get column by key
  const column = props.columns.find((x) => x.key === key);

  // Check if column is sortable
  if (column?.sortable && column.property) {
    // Set property to sort by
    pagination.order_by = column.property;
    pagination.asc = asc;
  } else {
    // Reset sorting
    pagination.order_by = props.order?.by ?? "id";
    pagination.asc = props.order?.asc ?? false;

    // Reset sortBy
    sortBy.value = [];
  }

  // Trigger server-side query with new sorting
  console.log("sorting ss ", pagination.order_by);
  updateQuery({
    filters: prevQuery.filters,
    searchQuery: prevQuery.searchQuery,
    pagination: pagination.deepCopy(),
  });
}

function loadPagination(): void {
  // Set default order
  pagination.order_by = props.order?.by ?? "id";
  pagination.asc = props.order?.asc ?? false;

  // Set default page size
  if (props.pageSize) {
    pagination.page_size = props.pageSize;
  }

  // Load pagination from query
  if (router.currentRoute.value.query.pagination) {
    pagination.fromRouteQuery(
      router.currentRoute.value.query.pagination as string,
    );
  } else if (props.entityName) {
    const result = filterPaginationStore.get(props.entityName);
    if (result) {
      pagination.fromObject(result.pagination);
      filters.value = result.filters;
    }
  }

  // Synchronize sortBy with loaded pagination
  syncSortByWithPagination();
}

/**
 * Synchronizes the sortBy ref with the current pagination state.
 * This ensures the table header sort icons match the actual sort order.
 */
function syncSortByWithPagination(): void {
  // Find the column that matches the current order_by property
  const column = props.columns.find((x) => x.property === pagination.order_by);
  
  if (column?.sortable && column.key) {
    sortBy.value = [{
      key: column.key,
      order: pagination.asc ? "asc" : "desc",
    }];
  } else {
    sortBy.value = [];
  }
}

function delayedQuery(): void {
  setTimeout(() => {
    load(prevQuery, true);
  }, 500);
}

function updatePageNumber(value: number): void {
  // Set the page number
  pagination.page_number = value;

  // Update the query
  updateQuery({
    ...prevQuery,
    pagination,
  });
}

function updatePageSize(value: number): void {
  // Set the page size
  pagination.page_size = value;

  // Update the query
  updateQuery({
    ...prevQuery,
    pagination,
  });
}

////////////////////////////////////////////////////////////
//////////////////// WATCH ///////////////////////////////
////////////////////////////////////////////////////////////
watch(
  () => pagination,
  () => {
    router.replace({
      query: {
        ...router.currentRoute.value.query,
        pagination: pagination.toRouteQuery(),
      },
    });
  },
  {
    deep: true,
  },
);

watch(
  () => sortBy.value,
  (value) => {
    // Skip during initialization
    if (isInitializing.value) {
      return;
    }

    if (value.length > 0) {
      // Get key and ordering
      const key = value[0].key;
      const asc = value[0].order === "asc";

      // Apply sorting
      setOrderBy(key, asc);
    }
  },
  {
    deep: true,
  },
);
////////////////////////////////////////////////////////////
//////////////////// COMPUTED //////////////////////////////
////////////////////////////////////////////////////////////
const hideSelect = computed(() => {
  if (Object.keys(props.multiItemsActions ?? {}).length === 0) {
    return true;
  }
  return props.hideSelect;
});

/**
 * Custom key sort functions that disable client-side sorting.
 * Returns 0 for all comparisons to preserve server-side sort order.
 * This ensures that sorting is always done server-side via the API.
 */
const customKeySort = computed(() => {
  const sortFunctions: Record<string, () => number> = {};
  for (const column of props.columns) {
    if (column.sortable && column.key) {
      sortFunctions[column.key] = () => 0;
    }
  }
  return sortFunctions;
});

/**
 * Returns the CSS class for a table row based on its properties
 * @param {TableRow} data - The row item
 * @returns {string} The CSS class for the row
 */
const getRowClass = (data: { item?: TableRow }): { class: string, title:string } | undefined => {
  if (data?.item?.warning) {
    const message = data?.item?.warningMessage || t("Warning");
    return {class: "warning-row", title: message};
  }
  if (data?.item?.selectable === false && data?.item?.selectableTooltip) {
    return {class: "non-selectable-row", title: data.item.selectableTooltip};
  }
  return undefined;
}

////////////////////////////////////////////////////////////
//////////////////// HOOKS /////////////////////////////////
////////////////////////////////////////////////////////////
onMounted(() => {
  // Get filters and pagination from query or store
  loadPagination();

  // Apply filters
  applyFilters(filters.value);

  // Enable watch after initialization
  isInitializing.value = false;

  // Listen for store updates
  const eventName = "store.update." + props.entityName;
  window.removeEventListener(eventName, delayedQuery);
  window.addEventListener(eventName, delayedQuery);
});

onBeforeUnmount(() => {
  const eventName = "store.update." + props.entityName;
  window.removeEventListener(eventName, delayedQuery);
});
</script>

<template>
  <div>
    <VRow v-if="!showFilterBar" class="mb-3">
      <!-- SELECTED ITEMS ACTIONS -->
      <VCol v-if="selected.length" md="6">
        <VBtn
          v-for="action in Object.values(props.multiItemsActions ?? {})"
          :color="action.color ?? 'dark'"
          class="mr-3"
          @click="
            () => {
              perform(action, selected);
              selected = [];
            }
          "
        >
          <VIcon class="mr-2">{{ action.icon }}</VIcon>
          {{ $t(action.label) }}
        </VBtn>
      </VCol>

      <!-- DEFAULT ACTIONS -->
      <VCol v-else cols="12" md="6">
        <VBtn
          v-for="action in Object.values(props.defaultActions ?? {})"
          class="mr-3"
          :color="action.color ?? 'dark'"
          @click="perform(action)"
        >
          <VIcon class="mr-2">{{ action.icon }}</VIcon>
          {{ $t(action.label) }}
        </VBtn>
      </VCol>

      <!-- ENABLE FILTER BAR -->
      <VCol
        v-if="!props.hideFilterBar"
        cols="12"
        md="2"
        class="d-flex justify-end"
      >
        <VBtn
          v-if="filters.length > 0"
          @click="() => (showFilterBar = !showFilterBar)"
          :color="filtersActive ? 'primary' : 'default'"
          icon
          outlined
        >
          <VIcon v-if="filtersActive">tabler-filter</VIcon>
          <VIcon v-else>tabler-filter-off</VIcon>
        </VBtn>
      </VCol>

      <!-- SEARCH BAR -->
      <VCol
        v-if="!props.hideFilterBar && !props.disableSearch"
        cols="12"
        md="4"
      >
        <AppTextField
          v-model="search"
          :placeholder="$t('Search') + '...'"
          append-inner-icon="tabler-search"
          :append-icon="search ? 'tabler-x' : undefined"
          single-line
          hide-details
          dense
          outlined
          @keyup.enter="performSearch"
          @click:append="() => { search = ''; performSearch(); }"
        />
      </VCol>
    </VRow>

    <!-- 👉 Filter Bar -->
    <FilterBar
      v-if="!props.hideFilterBar"
      v-model:show-filter-bar="showFilterBar"
      v-model:filters="filters"
      @apply-filters="applyFilters"
      @clear-filters="clearFilters"
    />

    <!-- 👉 Data Table  -->
    <VDataTable
      v-model="selected"
      :loading="loading"
      :headers="props.columns.map((x) => ({ ...x, title: $te(x.title) ? $t(x.title) : x.title }))"
      :items="tableRows"
      :items-per-page="pagination.page_size"
      v-model:sort-by="sortBy"
      :must-sort="true"
      :custom-key-sort="customKeySort"
      class="text-no-wrap"
      :show-select="!hideSelect"
      :item-selectable="(item: any) => item.selectable !== false"
      @click:row="props.rowClickHandler"
      :row-props="getRowClass"
    >
      <!-- Card -->
      <template #item.card="{ item }">
        <div class="d-flex align-center">
          <div>
            <VImg :src="item.data.product.image" height="40" width="40"/>
          </div>
          <div class="d-flex flex-column ms-3">
            <span
              class="d-block font-weight-medium text-truncate text-high-emphasis"
            >{{ item.data.product.name }}</span
            >
            <span class="text-xs">{{ item.data.product.brand }}</span>
          </div>
        </div>
      </template>

      <!-- Person -->
      <template #item.avatar="{ item }">
        <div class="d-flex align-center">
          <VAvatar
            size="2.3rem"
            :color="!item.data.avatar.url ? 'primary' : undefined"
            :variant="!item.data.avatar.url ? 'tonal' : undefined"
          >
            <VImg v-if="item.data.avatar.url" :src="item.data.avatar.url"/>
            <span v-else>{{
                (item.data.firstName && item.data.firstName.length > 0 ? item.data.firstName[0].toUpperCase() : '') +
                (item.data.lastName && item.data.lastName.length > 0 ? item.data.lastName[0].toUpperCase() : '')
              }}</span>
          </VAvatar>
          <span
            v-if="item.data.avatar.url"
            class="text-no-wrap font-weight-medium text-high-emphasis ms-2"
          >{{ item.data.firstName }}</span
          >
        </div>
      </template>

      <!-- Status -->
      <template #item.status="{ item }">
        <VChip
          :color="resolveStatusColor(item.data.status)"
          :class="`text-${resolveStatusColor(item.data.status)}`"
          size="small"
          class="font-weight-medium"
        >
          {{ $t(resolveStatusText(item.data.status)) }}
        </VChip>
      </template>

      <!-- Actions -->
      <template #item.actions="{ item }">
        <IconBtn
          v-for="action in Object.keys(item.actions)"
          :key="action"
          @click="execute(item.actions[action].handler, item)"
        >
          <VIcon :icon="item.actions[action].icon"/>
        </IconBtn>
      </template>

      <!-- Pagination -->
      <template #bottom>
        <VCardText class="pt-2">
          <div
            class="d-flex flex-wrap justify-center justify-sm-space-between gap-y-2 mt-2"
          >
            <AppSelect
              v-model="pagination.page_size"
              @update:model-value="updatePageSize"
              :label="$t('Rows per page')"
              type="number"
              min="-1"
              max="100"
              hide-details
              variant="underlined"
              style="max-inline-size: 8rem; min-inline-size: 5rem"
              :items="[10, 25, 50, 100]"
            />

            <VPagination
              class="mt-5"
              v-model="pagination.page_number"
              @update:model-value="updatePageNumber"
              :total-visible="1"
              :length="
                pagination.has_next_page
                  ? pagination.page_number + 1
                  : pagination.page_number
              "
            />
          </div>
        </VCardText>
      </template>
    </VDataTable>

    <VRow>
      <VCol cols="12" class="d-flex justify-end mt-2">
        <VBtn
          v-if="(tableRows.length === 0 && filtersActive) || searching"
          class="ml-3"
          color="dark"
          @click="clearFilters"
        >
          <VIcon class="mr-2">tabler-x</VIcon>
          {{ $t("Reset filters") }}
        </VBtn>
      </VCol>
    </VRow>
  </div>
</template>

<style lang="scss">
@use "@/assets/styles/styles.scss";

.v-data-table__tr.v-data-table__tr--clickable {
  &:hover {
    background-color: rgba(var(--v-theme-primary), 0.1);
  }

  &:active {
    background-color: rgba(var(--v-theme-primary), 0.2);
  }
}

.warning-row {
  background-color: rgba(var(--v-theme-warning), 0.15) !important;
}

.non-selectable-row {
  opacity: 0.6;
  cursor: not-allowed !important;
}
</style>
